/**
 * Przypisanie kodu rozstawu osi ({{versionWheelbase}}, np. "W07") dla
 * Mercedes Benz Tourist Line HighDeck.
 *
 * W przeciwieństwie do Iveco - gdzie kod W wynika z fizycznego rozstawu osi
 * [mm] + długości pojazdu, wybranych w opcjach zamówienia (patrz
 * iveco-wheelbases.ts + wheelbase-utils.ts) - dla tej zabudowy klient podał
 * przypisanie na podstawie CAŁKOWITEJ liczby pasażerów w wybranym wariancie
 * miejsc (order.places.variants[0].passengersCount), w ramach danego tonażu
 * ({{versionTonnage}}).
 *
 * UWAGA - część kombinacji tonaż+liczba pasażerów jest niejednoznaczna (kilka
 * różnych kodów W ma tę samą liczbę pasażerów w ramach tego samego tonażu),
 * a źródło rozróżnia je dodatkowym opisem (pole `note` niżej). Część tych
 * przypadków da się jednak rozstrzygnąć automatycznie dzięki polu
 * `allowsPilot` - dla kodów W, które w danych źródłowych występują TYLKO w
 * wariancie bez pilotki (`allowsPilot: false`), obecność pilotki
 * (`order.places.variants[0].pilotsCount`) od razu je wyklucza:
 * - tonaż 62, 16 pasażerów: W07 vs W09 (2 stoliki) - oba dopuszczają pilotkę,
 *   NIE da się rozstrzygnąć samą jej obecnością.
 * - tonaż 68, 24 pasażerów: W12 (z lub bez pilotki) vs W13 (tylko bez
 *   pilotki) - jeśli pilotka JEST, zostaje tylko W12 (rozstrzygnięte).
 * - tonaż 68, 26 pasażerów: W11 (tylko bez pilotki) vs W14 (tylko bez
 *   pilotki) - oba wykluczają pilotkę, więc jej obecność nie rozstrzyga (co
 *   więcej, sygnalizowałaby niezgodność z żadnym znanym wariantem).
 * Gdy po uwzględnieniu pilotki nadal zostaje więcej niż jeden kandydat (albo
 * został pusty zbiór - sprzeczne dane), zostają wszyscy kandydaci sprzed
 * filtrowania - {{versionWheelbase}} zostaje puste do ręcznego wyboru.
 * Komponent w takiej sytuacji pokazuje użytkownikowi
 * getMercedesHighDeckWheelbaseReferenceTable jako pomocniczą tabelkę
 * (kod W / opis), żeby dało się wybrać właściwy wariant ręcznie. Jeśli
 * pojawi się kolejne pole odróżniające warianty (np. liczba stolików w
 * opcjach zamówienia), WHEELBASE_INFO niżej można rozszerzyć analogicznie do
 * `allowsPilot`.
 *
 * @module mercedes-tourist-line-highdeck-wheelbase
 */

/** Jeden wpis: liczba pasażerów dla danego kodu W + opcjonalne dodatkowe informacje odróżniające warianty o tej samej liczbie pasażerów */
interface WheelbaseInfo {
  passengersCount: number;
  allowsPilot?: false;
  note?: string;
}

/** Tonaż -> kod W -> informacja o liczbie pasażerów (+ ew. dodatkowe info) */
const WHEELBASE_INFO: Record<string, Record<string, WheelbaseInfo>> = {
  "62": {
    W05: { passengersCount: 22 },
    W06: { passengersCount: 19 },
    W07: { passengersCount: 16 },
    W08: { passengersCount: 13, note: "4 stoliki" },
    W09: { passengersCount: 16, note: "2 stoliki" },
  },
  "68": {
    W10: { passengersCount: 22 },
    W11: { passengersCount: 26, allowsPilot: false, note: "bez pilotki" },
    W12: { passengersCount: 24, note: "z lub bez pilotki" },
    W13: {
      passengersCount: 24,
      allowsPilot: false,
      note: "bez pilotki + 4 siedzenia ustawione naprzeciw siebie",
    },
    W14: {
      passengersCount: 26,
      allowsPilot: false,
      note: "bez pilotki + 4 siedzenia ustawione naprzeciw siebie",
    },
  },
};

/** Sortuje kody W numerycznie (W05, W06, ... W14) */
function sortWheelbaseCodes(codes: string[]): string[] {
  return [...codes].sort(
    (a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10),
  );
}

/**
 * Odmienia "miejsce" wg polskich reguł dla liczb występujących w
 * WHEELBASE_INFO (11-19 i licz. zakończone na 2-4 poza 11-14 -> "miejsca",
 * pozostałe -> "miejsc").
 */
function pluralizeMiejsca(count: number): string {
  const lastTwoDigits = count % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return "miejsc";
  const lastDigit = count % 10;
  if (lastDigit >= 2 && lastDigit <= 4) return "miejsca";
  return "miejsc";
}

/**
 * Zwraca wszystkie kody W dla podanego tonażu, które mają dokładnie podaną
 * liczbę pasażerów - jeden element = jednoznaczne dopasowanie, więcej niż
 * jeden = niejednoznaczność (patrz komentarz modułu), chyba że obecność
 * pilotki (`pilotsCount`) zawęzi ich dalej (patrz `allowsPilot` w
 * WHEELBASE_INFO) - jeśli pilotka JEST, kody oznaczone `allowsPilot: false`
 * są wykluczane. Gdyby to zawężenie wyzerowało listę (dane sprzeczne z
 * WHEELBASE_INFO), zwracani są kandydaci sprzed zawężenia - lepiej pokazać
 * niejednoznaczność niż fałszywie puste dopasowanie.
 */
function findWheelbaseCandidates(
  tonnage: string | undefined | null,
  passengersCount: number | string | undefined | null,
  pilotsCount?: number | string | undefined | null,
): string[] {
  if (!tonnage || passengersCount === undefined || passengersCount === null) {
    return [];
  }
  const count = Number(passengersCount);
  if (!Number.isFinite(count)) {
    return [];
  }
  const byCode = WHEELBASE_INFO[tonnage];
  if (!byCode) return [];

  const candidates = Object.entries(byCode)
    .filter(([, info]) => info.passengersCount === count)
    .map(([code]) => code);

  const hasPilot = Number(pilotsCount) === 1;
  if (hasPilot && candidates.length > 1) {
    const withPilotAllowed = candidates.filter(
      (code) => byCode[code].allowsPilot !== false,
    );
    if (withPilotAllowed.length > 0) {
      return withPilotAllowed;
    }
  }

  return candidates;
}

/**
 * Wyznacza kod rozstawu osi ({{versionWheelbase}}) dla Mercedes Benz Tourist
 * Line HighDeck na podstawie tonażu, całkowitej liczby pasażerów i (jeśli to
 * rozstrzyga) obecności pilotki. Zwraca wynik tylko, gdy dopasowanie jest
 * jednoznaczne (dokładnie jeden kandydat).
 *
 * @param tonnage - Kod tonażu ({{versionTonnage}}, np. "62", "68")
 * @param passengersCount - Całkowita liczba pasażerów wariantu miejsc (order.places.variants[0].passengersCount)
 * @param pilotsCount - Liczba pilotów wariantu miejsc (order.places.variants[0].pilotsCount) - `1` = pilotka jest
 * @returns Kod rozstawu osi (np. "W05") lub null, jeśli brak jednoznacznego dopasowania
 */
export function getMercedesHighDeckWheelbase(
  tonnage: string | undefined | null,
  passengersCount: number | string | undefined | null,
  pilotsCount?: number | string | undefined | null,
): string | null {
  const candidates = findWheelbaseCandidates(
    tonnage,
    passengersCount,
    pilotsCount,
  );
  return candidates.length === 1 ? candidates[0] : null;
}

/**
 * Zwraca listę opcji rozstawu osi ({{versionWheelbase}}) do wyboru w
 * formularzu dla Mercedes Benz Tourist Line HighDeck, posortowaną numerycznie.
 *
 * Tonaż, liczba pasażerów i obecność pilotki są już znane w chwili
 * renderowania formularza (wynikają z danych zamówienia) - dlatego lista jest
 * od razu zawężona najpierw do wariantów W danego tonażu (np. tylko W05-W09
 * dla "62"), potem do kandydatów dla danej liczby pasażerów (np. tylko W11 i
 * W14 dla 26 pasażerów przy tonażu "68"), a na końcu - jeśli to rozstrzyga -
 * dodatkowo wg obecności pilotki (np. tylko W12 dla 24 pasażerów z pilotką
 * przy tonażu "68"). Gdy któryś z tych warunków nie zawęża niczego (albo
 * dane wejściowe nie są podane), lista wraca do szerszego zestawu - lepiej
 * pokazać więcej opcji niż żadnej.
 *
 * @param tonnage - Kod tonażu ({{versionTonnage}}, np. "62", "68")
 * @param passengersCount - Całkowita liczba pasażerów wariantu miejsc (order.places.variants[0].passengersCount)
 * @param pilotsCount - Liczba pilotów wariantu miejsc (order.places.variants[0].pilotsCount) - `1` = pilotka jest
 */
export function getMercedesHighDeckWheelbaseOptions(
  tonnage: string | undefined | null,
  passengersCount?: number | string | undefined | null,
  pilotsCount?: number | string | undefined | null,
): Array<{ value: string; label: string }> {
  if (!tonnage) return [];
  const byCode = WHEELBASE_INFO[tonnage];
  if (!byCode) return [];

  const candidates = findWheelbaseCandidates(
    tonnage,
    passengersCount,
    pilotsCount,
  );
  const codes = candidates.length > 0 ? candidates : Object.keys(byCode);

  return sortWheelbaseCodes(codes).map((code) => ({
    value: code,
    label: code,
  }));
}

/**
 * Zwraca tabelkę informacyjną (kod W / opis "N miejsc (uwaga)") dla
 * wszystkich wariantów danego tonażu - do pokazania użytkownikowi, gdy
 * getMercedesHighDeckWheelbase nie zwróciło jednoznacznego dopasowania
 * (niejednoznaczna liczba pasażerów, albo tonaż/liczba pasażerów spoza
 * WHEELBASE_INFO) - pozwala ręcznie wybrać właściwy wariant z listy opcji.
 *
 * @param tonnage - Kod tonażu ({{versionTonnage}}, np. "62", "68")
 */
export function getMercedesHighDeckWheelbaseReferenceTable(
  tonnage: string | undefined | null,
): Array<{ code: string; description: string }> {
  if (!tonnage) return [];
  const byCode = WHEELBASE_INFO[tonnage];
  if (!byCode) return [];
  return sortWheelbaseCodes(Object.keys(byCode)).map((code) => {
    const { passengersCount, note } = byCode[code];
    const description = `${passengersCount} ${pluralizeMiejsca(passengersCount)}${
      note ? ` (${note})` : ""
    }`;
    return { code, description };
  });
}
